/**
 * Geocodificador usando CDAU (Callejero Digital de Andalucía Unificado)
 * 
 * Proporciona geocodificación de alta precisión para direcciones andaluzas.
 * Preferido sobre CartoCiudad para Andalucía por:
 * - Coordenadas nativas UTM30 ETRS89 (sin transformación)
 * - Precisión a nivel de portal/edificio
 * - Actualización más frecuente para Andalucía
 * 
 * Cobertura: 785 municipios andaluces
 * Formato: WFS 2.0 / GeoJSON
 * 
 * @module services/geocoding/generic
 * @version 2.0.0 - Integración de errores tipados (TD-009)
 */

import type { AxiosInstance, AxiosError } from 'axios';
import axios from 'axios';
import { FastFuzzy } from '../../../lib/fuzzySearch';
import type { GeocodingResult as LegacyGeocodingResult } from '../../../types/infrastructure';
import type { GeocodingResult as TypedResult } from '../../../types/errors';
import { 
  createErrorContext, 
  createError, 
  success, 
  failure,
} from '../../../types/errors';
import type { GeocodedCoordinates } from './CartoCiudadGeocoder';

/**
 * Respuesta de CDAU WFS
 */
interface CDAUFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    INE_MUN?: string;
    MUNICIPIO?: string;
    PROVINCIA?: string;
    TIPO_VIA?: string;
    NOMBRE_VIA?: string;
    NUM_PORTAL?: number | string;
    COD_POSTAL?: string;
    X_ETRS89?: number;
    Y_ETRS89?: number;
    [key: string]: string | number | undefined;
  };
}

/**
 * Opciones para geocodificación CDAU
 */
export interface CDAUSearchOptions {
  /** Nombre de calle o dirección */
  street: string;
  /** Número de portal (opcional) */
  portalNumber?: string | number;
  /** Municipio (CRÍTICO para filtrado) */
  municipality: string;
  /** Provincia */
  province?: string;
  /** Máximo de resultados */
  maxResults?: number;
}

/**
 * Estadísticas del geocodificador
 */
interface CDAUStats {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  cacheHits: number;
  avgResponseTime: number;
}

/**
 * Geocodificador CDAU para Andalucía
 * 
 * @example
 * ```typescript
 * const geocoder = new CDAUGeocoder();
 * 
 * const result = await geocoder.geocode({
 *   street: 'Gran Vía',
 *   portalNumber: 1,
 *   municipality: 'Granada',
 *   province: 'Granada'
 * });
 * ```
 */
export class CDAUGeocoder {
  // WFS endpoint — vía retirada en CDAUWFS (24-sep-2026); se conserva para los mensajes de error y getStats
  private readonly WFS_ENDPOINT = 'https://www.callejerodeandalucia.es/servicios/cdau/wfs';
  
  // Endpoint alternativo via API REST de datos abiertos
  // NOTA: Este endpoint ya no está disponible (devuelve 404)
  // Se mantiene por si se reactiva en el futuro
  private readonly API_ENDPOINT = 'https://www.juntadeandalucia.es/datosabiertos/portal/api/3/action/datastore_search';
  private readonly RESOURCE_ID = 'cdau-portales';
  private readonly API_ENABLED = false; // Desactivado - API no disponible
  
  private axiosInstance: AxiosInstance;
  private cache: Map<string, LegacyGeocodingResult>;
  private stats: CDAUStats;
  
  constructor() {
    this.axiosInstance = axios.create({
      timeout: 15000,
      headers: {
        'Accept': 'application/json, application/xml',
      }
    });
    
    this.cache = new Map();
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      cacheHits: 0,
      avgResponseTime: 0
    };
  }

  /**
   * Geocodifica una dirección usando CDAU
   */
  public async geocode(options: CDAUSearchOptions): Promise<TypedResult<GeocodedCoordinates>> {
    console.log(`[CDAU] 📍 Iniciando geocodificación: "${options.street}" en ${options.municipality}`);
    
    const cacheKey = this.buildCacheKey(options);
    const startTime = Date.now();
    
    // Contexto de error para trazabilidad
    const errorContext = createErrorContext('cdau', 'L4', {
      elementName: options.street,
      municipio: options.municipality,
      provincia: options.province,
    });
    
    this.stats.totalRequests++;
    
    // Verificar caché
    if (this.cache.has(cacheKey)) {
      this.stats.cacheHits++;
      const cached = this.cache.get(cacheKey)!;
      return success(this.legacyToTyped(cached), {
        processingTime: Date.now() - startTime,
        sourcesConsulted: ['cdau'],
        finalSource: 'cache',
        cascadeLevel: 'L0',
        fromCache: true,
      });
    }

    try {
      // CDAUWFS (24-sep-2026): vía WFS retirada. Pedía una capa de portales que el
      // servidor ya no ofrece (400 en las 50 llamadas del arnés), y ni con la capa de hoy
      // (`cdau:v_portalpk`) aportaba: 32 de 33 consultas daban 0 portales porque se filtra
      // `nom_via` con la dirección entera. Los portales CDAU llegan por `cdau_r2`.
      // Detalle en .ptel/p0_cdauwfs_20260923/AUDIT_CDAUWFS.md.
      let result: LegacyGeocodingResult | null = null;

      // API REST de datos abiertos (si está habilitada)
      if (this.API_ENABLED) {
        result = await this.geocodeViaAPI(options);
      }

      if (result) {
        this.cache.set(cacheKey, result);
        this.stats.successfulRequests++;
        
        // Actualizar tiempo promedio
        const responseTime = Date.now() - startTime;
        this.stats.avgResponseTime = 
          (this.stats.avgResponseTime * (this.stats.totalRequests - 1) + responseTime) / 
          this.stats.totalRequests;
        
        return success(this.legacyToTyped(result), {
          processingTime: responseTime,
          sourcesConsulted: ['cdau'],
          finalSource: 'cdau',
          cascadeLevel: 'L4',
          fromCache: false,
        });
      }

      this.stats.failedRequests++;
      return failure(
        createError.noResults(
          errorContext,
          `${options.street} ${options.municipality}`,
          { municipality: options.municipality, province: options.province || '' },
          true
        )
      );

    } catch (error) {
      this.stats.failedRequests++;
      
      // Clasificar error de red
      const axiosError = error as AxiosError;
      
      if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
        return failure(
          createError.timeout(errorContext, this.WFS_ENDPOINT, 15000)
        );
      }
      
      if (axiosError.response?.status === 429) {
        return failure(
          createError.rateLimit(errorContext, this.WFS_ENDPOINT, 60)
        );
      }
      
      if (axiosError.response?.status && axiosError.response.status >= 500) {
        return failure(
          createError.serviceUnavailable(errorContext, this.WFS_ENDPOINT, axiosError.response.status)
        );
      }
      
      // CORS o conexión
      if (axiosError.message?.includes('CORS') || axiosError.message?.includes('Network')) {
        return failure(
          createError.cors(errorContext, this.WFS_ENDPOINT)
        );
      }
      
      return failure(
        createError.connection(errorContext, this.WFS_ENDPOINT, 'unknown')
      );
    }
  }

  /**
   * Convierte resultado legacy a formato tipado
   */
  private legacyToTyped(legacy: LegacyGeocodingResult): GeocodedCoordinates {
    return {
      x: legacy.x,
      y: legacy.y,
      confidence: legacy.confidence,
      source: legacy.source,
      sourceLayer: legacy.sourceLayer,
      matchedName: legacy.matchedName,
      matchType: legacy.matchType,
      fuzzyScore: legacy.fuzzyScore,
      address: legacy.address,
      municipality: legacy.municipality,
      muniCode: legacy.muniCode,
      province: legacy.province,
      metadata: legacy.metadata,
    };
  }

  /**
   * Geocodificación via API REST de datos abiertos
   */
  private async geocodeViaAPI(options: CDAUSearchOptions): Promise<LegacyGeocodingResult | null> {
    try {
      const filters: Record<string, string> = {
        MUNICIPIO: options.municipality.toUpperCase()
      };

      const params = new URLSearchParams({
        resource_id: this.RESOURCE_ID,
        filters: JSON.stringify(filters),
        q: options.street,
        limit: String(options.maxResults || 50)
      });

      const response = await this.axiosInstance.get(`${this.API_ENDPOINT}?${params.toString()}`);

      if (!response.data?.success || !response.data?.result?.records?.length) {
        return null;
      }

      // Convertir records a features
      const records = response.data.result.records;
      const features: CDAUFeature[] = records.map((r: Record<string, unknown>) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            parseFloat(String(r.X_ETRS89 || r.x_etrs89 || '0')),
            parseFloat(String(r.Y_ETRS89 || r.y_etrs89 || '0'))
          ]
        },
        properties: r
      })).filter((f: CDAUFeature) => 
        f.geometry.coordinates[0] !== 0 && f.geometry.coordinates[1] !== 0
      );

      if (features.length === 0) {
        return null;
      }

      const bestMatch = this.findBestMatch(options.street, features);
      
      if (!bestMatch) {
        return null;
      }

      return this.featureToResult(bestMatch.feature, bestMatch.score, options);

    } catch (error) {
      console.warn('CDAU API failed:', error);
      return null;
    }
  }

  /**
   * Encuentra el mejor match usando fuzzy matching
   */
  private findBestMatch(
    searchStreet: string,
    features: CDAUFeature[]
  ): { feature: CDAUFeature; score: number } | null {
    if (features.length === 0) return null;

    // Preparar datos para FastFuzzy (uFuzzy)
    const items = features.map(f => ({
      feature: f,
      name: f.properties.NOMBRE_VIA || ''
    }));

    const fuzzy = new FastFuzzy(items, {
      keys: ['name'],
      threshold: 0.6  // FastFuzzy: menor = más estricto
    });

    const results = fuzzy.search(searchStreet);

    if (results.length === 0) {
      // Si no hay match, devolver el primero
      return { feature: features[0], score: 0.5 };
    }

    const best = results[0];
    return {
      feature: best.item.feature,
      score: 1 - best.score
    };
  }

  /**
   * Convierte feature CDAU a resultado de geocodificación
   */
  private featureToResult(
    feature: CDAUFeature,
    score: number,
    options: CDAUSearchOptions
  ): LegacyGeocodingResult {
    const props = feature.properties;
    const coords = feature.geometry.coordinates;

    // Usar coordenadas de properties si están disponibles (más precisas)
    const x = props.X_ETRS89 || coords[0];
    const y = props.Y_ETRS89 || coords[1];

    // Calcular confianza
    let confidence = Math.round(score * 100);
    
    // Bonus si coincide el número de portal
    if (options.portalNumber && 
        String(props.NUM_PORTAL) === String(options.portalNumber)) {
      confidence = Math.min(confidence + 10, 98);
    }

    // Construir dirección completa
    const addressParts: string[] = [];
    if (props.TIPO_VIA) addressParts.push(props.TIPO_VIA);
    if (props.NOMBRE_VIA) addressParts.push(props.NOMBRE_VIA);
    if (props.NUM_PORTAL) addressParts.push(String(props.NUM_PORTAL));

    return {
      x,
      y,
      confidence,
      source: 'CDAU',
      sourceLayer: this.RESOURCE_ID,
      matchedName: props.NOMBRE_VIA || '',
      matchType: props.NUM_PORTAL ? 'portal' : 'street',
      fuzzyScore: score,
      address: addressParts.join(' '),
      municipality: props.MUNICIPIO,
      province: props.PROVINCIA,
      metadata: {
        ineMun: props.INE_MUN,
        postalCode: props.COD_POSTAL,
        streetType: props.TIPO_VIA
      }
    };
  }

  /**
   * Construye clave de caché
   */
  private buildCacheKey(options: CDAUSearchOptions): string {
    return `${options.street}|${options.portalNumber || ''}|${options.municipality}|${options.province || ''}`.toLowerCase();
  }

  /**
   * Limpia la caché
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Obtiene estadísticas
   */
  public getStats(): CDAUStats & { cacheSize: number; endpoint: string } {
    return {
      ...this.stats,
      cacheSize: this.cache.size,
      endpoint: this.WFS_ENDPOINT
    };
  }

  /**
   * Resetea estadísticas
   */
  public resetStats(): void {
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      cacheHits: 0,
      avgResponseTime: 0
    };
  }
}

export default CDAUGeocoder;